import { useDispatch, useSelector } from 'react-redux';
import { InView } from 'react-intersection-observer';
import { TableInstance } from 'react-table';
import {
  annotatorsListSelector,
  gridListSelector,
  isLoadingSelector,
  selectedAnnotatorsSelector,
  selectedEffortsSelector,
  selectedGridsSelector,
} from '../../selectors/admin.selectors';
import {
  Box,
  HbTable,
  HStack,
  Img,
  Text,
  Spinner,
  Button,
} from '@cdl/heartbeat-react';
import { CategoriesFilter } from './filters/CategoriesFilter';
import { handleParams } from '../../actions/admin.actions';
import { useMemo } from 'react';
import { AnnotationTask } from '../../../ecg/types/annotation-tasks.type';
import noStateImg from '../../../../assets/images/empty-state.svg';
import { effortEmojiMapping } from '../../utils/task.utils';
import { Link } from 'react-router-dom';

type Props = {
  instance: TableInstance<AnnotationTask>;
  incrementPage: () => void;
};

export const AnnotationTaskTable: React.FC<Props> = ({
  instance,
  incrementPage,
}) => {
  const dispatch = useDispatch();
  const annotatorsList = useSelector(annotatorsListSelector);
  const selectedAnnotators = useSelector(selectedAnnotatorsSelector);
  const gridList = useSelector(gridListSelector);
  const selectedGrids = useSelector(selectedGridsSelector);
  const selectedEfforts = useSelector(selectedEffortsSelector);
  const isLoading = useSelector(isLoadingSelector);

  const annotatorsOptions = useMemo(
    () =>
      annotatorsList.map(({ id: value, name: displayValue }) => ({
        value,
        displayValue,
      })),
    [annotatorsList],
  );

  const effortList = useMemo(
    () =>
      Object.keys(effortEmojiMapping).map((effort) => ({
        value: effort,
        displayValue: effortEmojiMapping[effort],
      })),
    [],
  );

  const onChangeAnnotators = (selectedData: string[]) => {
    dispatch(
      handleParams({ param: 'selectedAnnotators', value: selectedData }),
    );
  };

  const onChangeGrids = (selectedData: string[]) => {
    dispatch(handleParams({ param: 'selectedGrids', value: selectedData }));
  };

  const onChangeEfforts = (selectedData: string[]) => {
    dispatch(handleParams({ param: 'selectedEfforts', value: selectedData }));
  };

  return (
    <>
      <HStack my='l' justifyContent='space-between'>
        <HStack>
          <CategoriesFilter
            options={annotatorsOptions}
            type='Annotators'
            selectedOptions={selectedAnnotators}
            onChange={onChangeAnnotators}
          />
          <CategoriesFilter
            options={gridList}
            type='Annotation grid'
            selectedOptions={selectedGrids}
            onChange={onChangeGrids}
          />
          <CategoriesFilter
            options={effortList}
            type='Effort'
            selectedOptions={selectedEfforts}
            onChange={onChangeEfforts}
          />
        </HStack>
        <Link to={'/admin/create_task'}>
          <Button>+ New annotation task</Button>
        </Link>
      </HStack>
      <Box position='relative'>
        <HbTable
          instance={instance}
          estimateRowHeight={{ small: 200, default: 50 }}
        />
        {isLoading && (
          <Box
            display='flex'
            minH='300px'
            justifyContent='center'
            alignItems='flex-end'
            position='absolute'
            top={0}
            right={0}
            bottom={0}
            left={0}
            opacity={0.5}
            bgColor={instance.data.length === 0 ? '' : 'neutral.0'}
          >
            <Spinner color='primary.400' size='xl' mb='3.75rem' />
          </Box>
        )}
        {instance.data.length === 0 && !isLoading && (
          <Box pt='xxl'>
            <Img mx='auto' src={noStateImg} alt='No annotation tasks found' />
            <Text paddingTop='l' fontWeight='bold' align='center'>
              No annotation tasks found
            </Text>
          </Box>
        )}
        {instance.data.length > 0 && instance.data.length % 20 === 0 ? (
          <InView
            onChange={(inView) => {
              if (inView) incrementPage();
            }}
          />
        ) : null}
      </Box>
    </>
  );
};
